'use client';

import { useEffect } from 'react';
import { safeCaptureException, safeCaptureMessage } from '@/utils/sentry-utils';
import { isChunkLoadError, handleChunkLoadError } from '@/utils/chunk-load-error';

/**
 * 全局错误监听器组件
 *
 * 功能:
 * - 监听 window.onerror 事件(捕获同步错误)
 * - 监听 unhandledrejection 事件(捕获 Promise 错误)
 * - 自动处理 ChunkLoadError：清除缓存并刷新页面
 * - 不显示任何 UI,只记录日志
 * - 配合 ErrorBoundary 形成完整的错误捕获网
 *
 * 使用方式:在根布局中作为组件渲染
 * ```tsx
 * <GlobalErrorListener />
 * ```
 */
export default function GlobalErrorListener() {
  useEffect(() => {
    const handleGlobalError = async (event: ErrorEvent) => {
      const error = event.error;

      // 检查是否为 ChunkLoadError，清缓存并写入 sessionStorage 标记（由 RscVersionFix 处理）
      if (error && isChunkLoadError(error)) {
        console.warn('ChunkLoadError detected in window.onerror:', error);
        await handleChunkLoadError();
        event.preventDefault();
      }

      // 使用 console.warn 而不是 console.error，避免在生产环境中触发 instrumentation 的双重上报
      console.warn('🌐 Global Error Captured:', error || event.message);

      // 安全地上报到 Sentry
      // 即使 Sentry 服务挂掉，也不会影响应用运行
      if (error) {
        safeCaptureException(error, {
          tags: {
            errorSource: 'window.onerror',
            handler: 'GlobalErrorListener',
          },
          extra: {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          },
        });
      } else {
        // 如果没有 error 对象,捕获消息
        safeCaptureMessage(`[GlobalErrorListener] ${event.message}`, 'error');
      }

      // 可选:阻止浏览器默认错误弹窗(开发环境可能希望看到)
      // event.preventDefault();
    };

    /**
     * 处理未捕获的 Promise rejection
     */
    const handleUnhandledRejection = async (event: PromiseRejectionEvent) => {
      const reason = event.reason;

      // 检查是否为 ChunkLoadError，清缓存并写入 sessionStorage 标记（由 RscVersionFix 处理）
      if (reason instanceof Error && isChunkLoadError(reason)) {
        console.warn('ChunkLoadError detected in unhandledrejection:', reason);
        await handleChunkLoadError();
        event.preventDefault();
      }

      // 使用 console.warn 而不是 console.error，避免在生产环境中触发 instrumentation 的双重上报
      console.warn('🌐 Unhandled Promise Rejection:', reason);

      // 安全地上报到 Sentry
      // 即使 Sentry 服务挂掉，也不会影响应用运行
      if (reason instanceof Error) {
        safeCaptureException(reason, {
          tags: {
            errorSource: 'unhandledrejection',
            handler: 'GlobalErrorListener',
          },
          extra: {
            promiseRejection: true,
          },
        });
      } else {
        // 如果 reason 不是 Error 对象(比如字符串或其他值)
        safeCaptureMessage(
          `[GlobalErrorListener] Unhandled Rejection: ${String(reason)}`,
          'error'
        );
      }

      // 可选:阻止浏览器默认行为
      // event.preventDefault();
    };

    // 注册事件监听器
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // 清理函数:组件卸载时移除监听器
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // 不渲染任何 UI
  return null;
}
